import {
  EMPTY_SCRIPT_SELECTION,
  isQuoteSelectionComplete,
  isCashEquityExchange,
  isStrikeEditable,
  isCategoryEditable,
  isExpiryEditable,
  optionTypeForApi,
  strikePriceForApi,
} from "./utils";

export function serializeScriptSelectionsScripKey(scriptSelections) {
  return JSON.stringify(
    (scriptSelections || []).map((s) => ({
      exchange: s.exchange ?? "",
      segment: s.segment ?? "",
      symbol: s.symbol ?? "",
      option_type: s.option_type ?? "",
      strike_price: s.strike_price ?? "",
      expiry_date: s.expiry_date ?? "",
    }))
  );
}

export function scriptDropdownValuesFromSelections(scriptSelections) {
  return (scriptSelections || []).map((s) => ({
    exchange: s.exchange,
    segment: s.segment,
    symbol: s.symbol,
    option_type: s.option_type,
    strike_price: s.strike_price,
    expiry_date: s.expiry_date,
  }));
}

export function serializeOrderInputKey(scriptSelections) {
  return JSON.stringify(
    (scriptSelections || []).map((s) => ({
      price: s.price ?? "",
      qtyLots: s.qtyLots ?? "",
      absDisQty: s.absDisQty ?? "",
      transactionType: s.transactionType ?? "",
      productType: s.productType ?? "",
      orderType: s.orderType ?? "",
      validity: s.validity ?? "",
      strategy: s.strategy ?? "",
    }))
  );
}

export function buildOrderInitiationConfigRequestList(sel, exchangeOnlyIndexes = null) {
  if (!sel?.length) {
    return [{ exchange: "", segment: "", symbol: "", option_type: "", strike_price: "", expiry_date: "" }];
  }

  // Prefer a Set/array of row indexes. Boolean `true` clears dependents for every row (legacy).
  let onlySet = null;
  if (exchangeOnlyIndexes === true) {
    onlySet = new Set(sel.map((_, i) => i));
  } else if (exchangeOnlyIndexes instanceof Set) {
    onlySet = exchangeOnlyIndexes;
  } else if (Array.isArray(exchangeOnlyIndexes)) {
    onlySet = new Set(exchangeOnlyIndexes);
  } else if (typeof exchangeOnlyIndexes === "number" && Number.isFinite(exchangeOnlyIndexes)) {
    onlySet = new Set([exchangeOnlyIndexes]);
  }

  return sel.map((s, i) => {
    const clearDependents = onlySet != null && onlySet.has(i);
    if (clearDependents) {
      return {
        exchange: (s.exchange ?? "").toString().trim(),
        segment: "",
        symbol: "",
        option_type: "",
        strike_price: "",
        expiry_date: "",
      };
    }
    return {
      exchange: (s.exchange ?? "").toString().trim(),
      segment: (s.segment ?? "").toString().trim(),
      symbol: (s.symbol ?? "").toString().trim(),
      option_type: optionTypeForApi(s),
      strike_price: strikePriceForApi(s),
      expiry_date: (s.expiry_date ?? "").toString().trim(),
    };
  });
}

export function buildDefaultSelection(opts) {
  if (!opts) return null;
  const ex = opts.unique_exchange_options;
  const seg = opts.unique_segment_options;
  const sym = opts.unique_symbol_options;
  const exp = opts.unique_expiry_date_options;
  const strikes = opts.unique_strike_price_options;
  const cat = opts.unique_option_type_options;
  if (!ex?.length || !seg?.length || !sym?.length) {
    return null;
  }
  const exchange0 = String(ex[0] ?? "");
  const segment0 = String(pickSegmentForExchange(exchange0, "", seg) ?? seg[0] ?? "");
  const cash = isCashEquityExchange(exchange0);
  if (!cash && !exp?.length) {
    return null;
  }
  let optionType = "";
  if (isCategoryEditable(exchange0, segment0)) {
    optionType = cat?.length ? cat[0] : "";
  }
  let strikePrice = "";
  if (isStrikeEditable(exchange0, segment0)) {
    if (!strikes?.length) return null;
    const strikeVal = strikes[0];
    const numStrike = strikeVal === "" || strikeVal == null ? NaN : Number(strikeVal);
    strikePrice = Number.isNaN(numStrike) ? "" : numStrike;
  }
  return {
    ...EMPTY_SCRIPT_SELECTION,
    exchange: ex[0],
    segment: segment0,
    symbol: sym[0],
    option_type: optionType,
    strike_price: strikePrice,
    expiry_date: cash ? "" : exp[0],
    transactionType: "Buy",
  };
}

function pickFirstOrKeep(current, list) {
  const cur = current == null ? "" : String(current).trim();
  // Empty options must not wipe a still-valid selection on another scrip.
  if (!list?.length) return current ?? "";
  if (cur && list.some((item) => String(item) === cur)) return current;
  return list[0];
}

/** NSE/BSE cash: prefer Scrip Type that matches the exchange when the API list includes it. */
function pickSegmentForExchange(exchange, current, list) {
  const cur = current == null ? "" : String(current).trim();
  if (!list?.length) return current ?? "";
  if (cur && list.some((item) => String(item) === cur)) return current;

  if (isCashEquityExchange(exchange)) {
    const ex = String(exchange ?? "").trim().toUpperCase();
    const match = list.find((item) => String(item ?? "").trim().toUpperCase() === ex);
    if (match != null && String(match).trim() !== "") return match;
  }
  return list[0];
}

/**
 * After dropdown-options API returns, keep still-valid values and fill blanks /
 * stale fields with the first option (same as earlier Order Initiate behaviour).
 */
export function applyDropdownFirstOptions(sel, opts) {
  if (!opts || !sel) return sel;

  const exchange = pickFirstOrKeep(sel.exchange, opts.unique_exchange_options);
  const segment = pickSegmentForExchange(exchange, sel.segment, opts.unique_segment_options);
  const symbol = pickFirstOrKeep(sel.symbol, opts.unique_symbol_options);

  let optionType = "";
  let strikePrice = "";
  let expiryDate = "";

  if (isCategoryEditable(exchange, segment)) {
    optionType = pickFirstOrKeep(sel.option_type, opts.unique_option_type_options);
  }
  if (isStrikeEditable(exchange, segment)) {
    const strike = pickFirstOrKeep(sel.strike_price, opts.unique_strike_price_options);
    strikePrice =
      strike === "" || strike == null || Number.isNaN(Number(strike)) ? "" : Number(strike);
  }
  if (isExpiryEditable(exchange, segment)) {
    expiryDate = pickFirstOrKeep(sel.expiry_date, opts.unique_expiry_date_options);
  }

  return {
    ...sel,
    exchange,
    segment,
    symbol,
    option_type: optionType,
    strike_price: strikePrice,
    expiry_date: expiryDate,
  };
}

export function mergeSelectionsWithDropdownDefaults(prev, optsList, { onlyIndexes = null } = {}) {
  if (!optsList.length) return prev;
  if (prev.length === 0) {
    const defaultSelection = buildDefaultSelection(optsList[0]);
    return defaultSelection ? [defaultSelection] : prev;
  }

  let onlySet = null;
  if (onlyIndexes instanceof Set) {
    onlySet = onlyIndexes;
  } else if (Array.isArray(onlyIndexes)) {
    onlySet = new Set(onlyIndexes);
  } else if (typeof onlyIndexes === "number" && Number.isFinite(onlyIndexes)) {
    onlySet = new Set([onlyIndexes]);
  }

  return prev.map((sel, i) => {
    // When exchange changed on one row, never rewrite untouched scrips.
    if (onlySet != null && !onlySet.has(i)) return sel;
    const opts = optsList[i];
    if (!opts) return sel;
    return applyDropdownFirstOptions(sel, opts);
  });
}

/**
 * Build get_quote_data rows from stored selection only (empties stay "").
 * Incomplete rows (after TriggeredBy clears) are omitted so we never re-send
 * old Segment/Symbol/etc. — call dropdown-options instead until the user fills again.
 */
export function buildQuoteDataRequestListResolved(scriptSelections) {
  const rows = scriptSelections || [];
  if (!rows.length) return [];

  return rows
    .filter((sel) => isQuoteSelectionComplete(sel))
    .map((sel) => {
      return {
        exchange: String(sel.exchange ?? "").trim(),
        segment: String(sel.segment ?? "").trim(),
        symbol: String(sel.symbol ?? "").trim(),
        option_type: optionTypeForApi(sel),
        strike_price: strikePriceForApi(sel),
        expiry_date: String(sel.expiry_date ?? "").trim(),
      };
    });
}
